import { supabase } from '@/lib/supabase';

export interface ProgressiveBacktestConfig {
  fileId: string;
  startPosition: number;
  maxSequenceLength: number;
  winThreshold: number; // 0.52 = 52%
  analysisMode: 'basic' | 'intermediate' | 'advanced';
  customConfig?: any;
}

export interface BacktestPosition {
  position: number;
  sequenceUsed: string;
  sequenceLength: number;
  actualResult: number; // 0 or 1
  prediction: 'WIN' | 'LOSS' | 'SKIP';
  winProbability: number;
  lossProbability: number;
  confidenceLevel: string;
  matchingPatterns: number;
  isCorrect?: boolean;
  reason: string;
  skipAnalysis?: {
    actualOutcome: 'WIN' | 'LOSS';
    wouldBeCorrect: boolean;
    skipType: 'GOOD_SKIP' | 'MISSED_OPPORTUNITY';
    potentialPrediction: 'WIN' | 'LOSS';
  };
}

export interface ProgressiveBacktestResult {
  fileId: string;
  configuration: ProgressiveBacktestConfig;
  totalPositions: number;
  predictedPositions: number;
  skippedPositions: number;
  correctPredictions: number;
  accuracy: number;
  skipRate: number;
  positions: BacktestPosition[];
  learningCurve: Array<{
    position: number;
    runningAccuracy: number;
    predictionsCount: number;
  }>;
  processingTime: number;
  skipAnalytics?: {
    totalSkips: number;
    goodSkips: number;
    missedOpportunities: number;
    skipEfficiency: number; // % of good skips
    potentialAccuracy: number; // if didn't skip missed opportunities
    skipBreakdown: {
      goodSkipWins: number; // correctly avoided losses
      goodSkipLosses: number; // correctly avoided wins (rare)
      missedWins: number;
      missedLosses: number;
    };
  };
}

export interface StoredBacktestSimulation {
  id: string;
  file_id: string;
  user_id: string;
  start_position: number;
  max_sequence_length: number;
  win_threshold: number;
  analysis_mode: string;
  total_positions: number;
  predicted_positions: number;
  skipped_positions: number;
  correct_predictions: number;
  accuracy: number;
  skip_rate: number;
  positions_data: BacktestPosition[];
  learning_curve: Array<{
    position: number;
    runningAccuracy: number;
    predictionsCount: number;
  }>;
  processing_time_ms: number;
  skip_analytics?: {
    totalSkips: number;
    goodSkips: number;
    missedOpportunities: number;
    skipEfficiency: number;
    potentialAccuracy: number;
    skipBreakdown: {
      goodSkipWins: number;
      goodSkipLosses: number;
      missedWins: number;
      missedLosses: number;
    };
  };
  created_at: string;
  updated_at: string;
}

export class ProgressiveBacktestService {
  /**
   * Run a progressive backtest simulation
   */
  static async runBacktest(
    config: ProgressiveBacktestConfig,
    onProgress?: (progress: { completed: number; total: number; accuracy: number }) => void
  ): Promise<{ success: boolean; result?: ProgressiveBacktestResult; error?: string }> {
    try {
      console.log('🚀 Starting Progressive Backtest with config:', config);

      const { data, error } = await supabase.functions.invoke('progressive-backtest', {
        body: config
      });

      if (error) {
        console.error('Edge Function error:', error);
        return { success: false, error: error.message };
      }

      if (!data || !data.success) {
        console.error('Backtest failed:', data);
        return { success: false, error: data?.error || 'Backtest failed' };
      }

      const result = data.result as ProgressiveBacktestResult;
      
      // Store the simulation in database
      await this.storeSimulation(result);

      console.log('✅ Progressive Backtest completed successfully');
      return { success: true, result };

    } catch (error) {
      console.error('Error running progressive backtest:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Store backtest simulation in database
   */
  private static async storeSimulation(result: ProgressiveBacktestResult): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Calculate skip analytics
      const skipAnalytics = this.calculateSkipAnalytics(result.positions);

      const { error } = await supabase
        .from('progressive_backtest_simulations')
        .insert([{
          file_id: result.fileId,
          user_id: user.id,
          start_position: result.configuration.startPosition,
          max_sequence_length: result.configuration.maxSequenceLength,
          win_threshold: result.configuration.winThreshold,
          analysis_mode: result.configuration.analysisMode,
          custom_config: result.configuration.customConfig,
          total_positions: result.totalPositions,
          predicted_positions: result.predictedPositions,
          skipped_positions: result.skippedPositions,
          correct_predictions: result.correctPredictions,
          accuracy: result.accuracy,
          skip_rate: result.skipRate,
          positions_data: result.positions,
          learning_curve: result.learningCurve,
          processing_time_ms: result.processingTime,
          skip_analytics: skipAnalytics
        }]);

      if (error) {
        console.error('Error storing simulation:', error);
        // Don't throw - simulation was successful, storage is optional
      }
    } catch (error) {
      console.error('Error in storeSimulation:', error);
      // Don't throw - simulation was successful, storage is optional
    }
  }

  /**
   * Get stored backtest simulations for a file
   */
  static async getSimulations(fileId: string): Promise<{ 
    success: boolean; 
    data?: StoredBacktestSimulation[]; 
    error?: string 
  }> {
    try {
      const { data, error } = await supabase
        .rpc('get_progressive_backtest_simulations', { p_file_id: fileId });

      if (error) {
        console.error('Error fetching simulations:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getSimulations:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get detailed simulation data
   */
  static async getSimulationDetail(simulationId: string): Promise<{
    success: boolean;
    data?: StoredBacktestSimulation;
    error?: string;
  }> {
    try {
      const { data, error } = await supabase
        .from('progressive_backtest_simulations')
        .select('*')
        .eq('id', simulationId)
        .single();

      if (error) {
        console.error('Error fetching simulation detail:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getSimulationDetail:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Delete a backtest simulation
   */
  static async deleteSimulation(simulationId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { error } = await supabase
        .from('progressive_backtest_simulations')
        .delete()
        .eq('id', simulationId);

      if (error) {
        console.error('Error deleting simulation:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in deleteSimulation:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Validate backtest configuration
   */
  static validateConfig(config: Partial<ProgressiveBacktestConfig>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!config.fileId) {
      errors.push('File ID is required');
    }

    if (config.startPosition === undefined || config.startPosition < 0) {
      errors.push('Start position must be >= 0');
    }

    if (!config.maxSequenceLength || config.maxSequenceLength < 3 || config.maxSequenceLength > 15) {
      errors.push('Max sequence length must be between 3 and 15');
    }

    if (!config.winThreshold || config.winThreshold < 0.5 || config.winThreshold > 0.9) {
      errors.push('Win threshold must be between 50% and 90%');
    }

    if (!config.analysisMode || !['basic', 'intermediate', 'advanced'].includes(config.analysisMode)) {
      errors.push('Analysis mode must be basic, intermediate, or advanced');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Calculate skip analytics from backtest results
   */
  static calculateSkipAnalytics(positions: BacktestPosition[]): {
    totalSkips: number;
    goodSkips: number;
    missedOpportunities: number;
    skipEfficiency: number;
    potentialAccuracy: number;
    skipBreakdown: {
      goodSkipWins: number;
      goodSkipLosses: number;
      missedWins: number;
      missedLosses: number;
    };
  } {
    const skippedPositions = positions.filter(pos => pos.prediction === 'SKIP');
    const totalSkips = skippedPositions.length;
    
    if (totalSkips === 0) {
      return {
        totalSkips: 0,
        goodSkips: 0,
        missedOpportunities: 0,
        skipEfficiency: 0,
        potentialAccuracy: 0,
        skipBreakdown: {
          goodSkipWins: 0,
          goodSkipLosses: 0,
          missedWins: 0,
          missedLosses: 0
        }
      };
    }

    let goodSkips = 0;
    let missedOpportunities = 0;
    let goodSkipWins = 0;
    let goodSkipLosses = 0;
    let missedWins = 0;
    let missedLosses = 0;

    // Analyze each skipped position
    skippedPositions.forEach(pos => {
      if (!pos.skipAnalysis) return;

      const { skipType, actualOutcome } = pos.skipAnalysis;
      
      if (skipType === 'GOOD_SKIP') {
        goodSkips++;
        if (actualOutcome === 'WIN') goodSkipWins++;
        else goodSkipLosses++;
      } else {
        missedOpportunities++;
        if (actualOutcome === 'WIN') missedWins++;
        else missedLosses++;
      }
    });

    const skipEfficiency = totalSkips > 0 ? (goodSkips / totalSkips) * 100 : 0;
    
    // Calculate potential accuracy if we didn't skip missed opportunities
    const totalPredicted = positions.filter(pos => pos.prediction !== 'SKIP').length;
    const correctPredictions = positions.filter(pos => pos.isCorrect === true).length;
    const potentialCorrect = correctPredictions + missedWins;
    const potentialTotal = totalPredicted + missedOpportunities;
    const potentialAccuracy = potentialTotal > 0 ? (potentialCorrect / potentialTotal) * 100 : 0;

    return {
      totalSkips,
      goodSkips,
      missedOpportunities,
      skipEfficiency,
      potentialAccuracy,
      skipBreakdown: {
        goodSkipWins,
        goodSkipLosses,
        missedWins,
        missedLosses
      }
    };
  }

  /**
   * Export backtest results to CSV with skip analysis
   */
  static exportToCSV(result: ProgressiveBacktestResult): string {
    const headers = [
      'Position',
      'Sequence Used',
      'Sequence Length',
      'Actual Result',
      'Prediction',
      'Win Probability',
      'Loss Probability',
      'Confidence Level',
      'Matching Patterns',
      'Is Correct',
      'Reason',
      'Skip Type',
      'Would Be Correct',
      'Potential Prediction'
    ];

    const csvData = result.positions.map(pos => [
      pos.position,
      pos.sequenceUsed,
      pos.sequenceLength,
      pos.actualResult === 1 ? 'WIN' : 'LOSS',
      pos.prediction,
      pos.winProbability.toFixed(2),
      pos.lossProbability.toFixed(2),
      pos.confidenceLevel,
      pos.matchingPatterns,
      pos.isCorrect !== undefined ? (pos.isCorrect ? 'YES' : 'NO') : 'SKIPPED',
      pos.reason,
      pos.skipAnalysis?.skipType || '',
      pos.skipAnalysis?.wouldBeCorrect !== undefined ? (pos.skipAnalysis.wouldBeCorrect ? 'YES' : 'NO') : '',
      pos.skipAnalysis?.potentialPrediction || ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    return csvContent;
  }
}

// Note: ProgressiveBacktestService.ts is getting quite long (308+ lines).
// Consider refactoring into smaller, focused modules after completing current features.